import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  MessageCircle, Send, Loader2, RefreshCw, User,
  ChevronLeft, Clock, Instagram, Facebook, Phone
} from 'lucide-react';

const API = '/api';

function PlatformIcon({ platform, className = "h-4 w-4" }) {
  if (platform === 'instagram') return <Instagram className={`${className} text-pink-500`} />;
  if (platform === 'whatsapp') return <Phone className={`${className} text-green-500`} />;
  return <Facebook className={`${className} text-blue-500`} />;
}

function PlatformBadge({ platform }) {
  const colors = {
    facebook: 'bg-blue-100 text-blue-700',
    instagram: 'bg-pink-100 text-pink-700',
    whatsapp: 'bg-green-100 text-green-700',
  };
  return (
    <Badge className={`${colors[platform] || 'bg-slate-100 text-slate-700'} text-[10px] px-1.5 py-0`}>
      {platform}
    </Badge>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function MessagingInbox() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');
  const messagesEndRef = useRef(null);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/messaging/inbox`, { withCredentials: true });
      setConversations(data.conversations || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const openConversation = async (conv) => {
    setSelected(conv);
    setMessages([]);
    setReplyText('');
    setLoadingMessages(true);
    try {
      const endpoint = conv.platform === 'instagram'
        ? `${API}/messaging/instagram/conversations/${conv.id}/messages`
        : `${API}/messaging/facebook/conversations/${conv.id}/messages`;
      const { data } = await axios.get(endpoint, { withCredentials: true });
      setMessages((data.messages || []).reverse());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load messages');
    } finally {
      setLoadingMessages(false);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      const endpoint = selected.platform === 'instagram'
        ? `${API}/messaging/instagram/conversations/${selected.id}/reply`
        : `${API}/messaging/facebook/conversations/${selected.id}/reply`;
      await axios.post(endpoint, {
        message: replyText.trim(),
        recipient_id: selected.customer_id,
      }, { withCredentials: true });
      toast.success('Reply sent!');
      setReplyText('');
      openConversation(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const filtered = filter === 'all' ? conversations : conversations.filter(c => c.platform === filter);

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="messaging-inbox">
      {/* Sidebar — Conversation List */}
      <div className={`${selected ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-[340px] border-r border-slate-200`}>
        {/* Header */}
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-slate-700" />
              <h2 className="text-base font-semibold text-slate-800">Inbox</h2>
              {conversations.length > 0 && (
                <Badge variant="secondary" className="text-[10px]">{conversations.length}</Badge>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={fetchInbox} disabled={loading} data-testid="refresh-inbox-btn">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {/* Platform Filter */}
          <div className="flex gap-1.5">
            {['all', 'facebook', 'instagram'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-colors ${
                  filter === f ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
                data-testid={`filter-${f}`}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 px-4">
              <MessageCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-400">No conversations yet</p>
            </div>
          )}
          {filtered.map(conv => (
            <button
              key={conv.id}
              onClick={() => openConversation(conv)}
              className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                selected?.id === conv.id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
              }`}
              data-testid={`conv-${conv.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                  <PlatformIcon platform={conv.platform} className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-800 truncate">{conv.customer_name}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{timeAgo(conv.updated_time)}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{conv.snippet || `${conv.message_count} messages`}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <PlatformBadge platform={conv.platform} />
                    {conv.unread_count > 0 && (
                      <Badge className="bg-red-500 text-white text-[9px] px-1.5 py-0">{conv.unread_count}</Badge>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main — Messages */}
      <div className={`${selected ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-slate-300">
            <div className="text-center">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Select a conversation</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white">
              <Button
                size="sm"
                variant="ghost"
                className="md:hidden"
                onClick={() => setSelected(null)}
                data-testid="back-to-list"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                <PlatformIcon platform={selected.platform} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-slate-800 truncate">{selected.customer_name}</h3>
                <div className="flex items-center gap-1.5">
                  <PlatformBadge platform={selected.platform} />
                  <span className="text-[10px] text-slate-400">{selected.message_count} messages</span>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/50">
              {loadingMessages && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              )}
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`flex ${msg.is_page ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] ${msg.is_page
                    ? 'bg-slate-900 text-white rounded-2xl rounded-br-sm'
                    : 'bg-white text-slate-800 border border-slate-200 rounded-2xl rounded-bl-sm'
                  } px-3.5 py-2.5 shadow-sm`}>
                    {!msg.is_page && (
                      <p className="text-[10px] font-medium text-slate-400 mb-0.5 flex items-center gap-1">
                        <User className="h-2.5 w-2.5" /> {msg.sender_name}
                      </p>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                    {msg.attachments?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {msg.attachments.map((att, i) => (
                          att.url && <img key={i} src={att.url} alt="attachment" className="max-w-full rounded-lg" />
                        ))}
                      </div>
                    )}
                    <p className={`text-[9px] mt-1 ${msg.is_page ? 'text-slate-400' : 'text-slate-300'} flex items-center gap-1`}>
                      <Clock className="h-2 w-2" /> {new Date(msg.created_time).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Input */}
            <div className="p-3 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <Input
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }}}
                  placeholder={`Reply to ${selected.customer_name}...`}
                  className="flex-1 text-sm"
                  disabled={sending}
                  data-testid="reply-input"
                />
                <Button
                  onClick={sendReply}
                  disabled={!replyText.trim() || sending}
                  className="shrink-0"
                  data-testid="send-reply-btn"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
